import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { toFixed, toFormat, times } from '@/utils/number';
import { hasValue } from '@/utils/tools';
import { accounts } from '@/config/loan';

export function useToken() {
    const store = useStore();
    const route = useRoute();
    const urlParams = route.query;
    const { currency, account } = urlParams;
    // 借贷币种列表(所有现货借贷币种)
    const loanTokens = computed(() => store.state.loan.loanTokens || []);
    // 借贷币种其他账户支持列表
    const accountLoanTokens = computed(() => store.state.loan.accountLoanTokens || []);
    // 质押币种列表(所有币种)
    const pledgeTokens = computed(() => store.state.loan.pledgeTokens || []);
    // 质押币种列表(只包含生效币种)
    const pledgeEnabledTokens = computed(() => pledgeTokens.value.filter((item) => item.enabled === 'Y'));
    // 借贷币种列表map
    const loanTokensMap = computed(() => store.getters['loan/loanTokensMap'] || {});
    // 借贷币种其他账户列表map
    const accountLoanTokensMap = computed(() => store.getters['loan/accountLoanTokensMap'] || {});
    // 质押币种列表map
    const pledgeTokensMap = computed(() => store.getters['loan/pledgeTokensMap'] || {});
    // 资产币种列表map
    const tokens = computed(() => store.getters['settings/assetCurrencyMap'] || {});
    // 行情币种map
    const tokensMap = computed(() => store.getters['loan/tokensMap']);
    // 借贷币种列表(只包含生效币种，处理了账户逻辑)
    const loanEnabledTokens = computed(() => {
        const isEnabledAccount = store.getters['loan/isEnabledAccount'];
        const allTokens = loanTokens.value.filter((item) => item.enabled === 'Y');
        if (isEnabledAccount && accountLoanTokens.value.length) {
            return accountLoanTokens.value;
        }
        return allTokens;
    });
    // 借贷币种默认币种&是否可切换
    const defaultLoanToken = computed(() => {
        const currentAccount = store.state.loan.account;
        const isEnabledAccount = store.getters['loan/isEnabledAccount'];
        if (!currentAccount || !loanEnabledTokens.value.length) return null;
        // [无账户 || 现货账户 || 其他无效账户] && 有币种参数，定位具体币种
        if ((!account || account === 'spot' || !accounts[account]) && currency) {
            // 币种列表包含 && 生效
            if (loanTokensMap.value[currency] && loanTokensMap.value[currency].enabled === 'Y') {
                return {
                    token: currency,
                    disabled: false,
                };
            }
        }
        // 有效账户 && 有效币种
        if (isEnabledAccount && currency) {
            if (!Object.keys(accountLoanTokensMap.value).length) {
                return {};
            }
            if (accountLoanTokensMap.value[currency]) {
                return {
                    token: currency,
                    disabled: true,
                };
            }
        }
        return {
            token: loanEnabledTokens.value[0]?.token,
            disabled: false,
        };
    });

    return {
        tokens,
        tokensMap,
        loanTokens,
        pledgeTokens,
        loanTokensMap,
        pledgeTokensMap,
        loanEnabledTokens,
        pledgeEnabledTokens,
        accountLoanTokensMap,
        defaultLoanToken,
    };
}

export function useNum(value) {
    return Number(value) || 0;
}

export function usePrecision() {
    const {
        tokens,
        loanTokensMap,
        pledgeTokensMap,
    } = useToken();

    return {
        loan: (token) => useNum(loanTokensMap.value[token]?.loanPrecision),
        interest: (token) => useNum(loanTokensMap.value[token]?.interestPrecision),
        liquidationPrice: (token) => useNum(loanTokensMap.value[token]?.liquidationPricePrecision),
        pledge: (token) => useNum(pledgeTokensMap.value[token]?.pledgePrecision),
        accuracy: (token) => useNum(tokens.value[token]?.accuracy),
    };
}

export function useFormat() {
    const {
        loan,
        interest,
        liquidationPrice,
        pledge,
        accuracy,
    } = usePrecision();

    const toFixedLtv = (value) => toFixed(times(value, 100), 0, 0);

    const toFixedToken = (value, token) => (hasValue(value) ? toFixed(value, accuracy(token)) : '-');
    const toFormatToken = (value, token) => (hasValue(value) ? toFormat(value, accuracy(token)) : '-');

    const toFixedLoan = (value, token, rm = 1) => (hasValue(value) ? toFixed(value, loan(token), rm) : '-');
    const toFormatLoan = (value, token, rm = 1) => (hasValue(value) ? toFormat(value, loan(token), rm) : '-');

    const toFixedInterest = (value, token) => (hasValue(value) ? toFixed(value, interest(token)) : '-');
    const toFormatInterest = (value, token, rm = 1) => (hasValue(value) ? toFormat(value, interest(token), rm) : '-');

    const toFixedLiquidate = (value, token) => (hasValue(value) ? toFixed(value, liquidationPrice(token)) : '-');

    const toFormatLiquidate = (value, token, showNum = true) => {
        if (!showNum) {
            return Number(value) !== 0 ? toFormat(value, liquidationPrice(token)) : '-';
        }
        return hasValue(value) ? toFormat(value, liquidationPrice(token)) : '-';
    };

    const toFixedPledge = (value, token, rm = 1) => (hasValue(value) ? toFixed(value, pledge(token), rm) : '-');
    const toFormatPledge = (value, token, rm = 1) => (hasValue(value) ? toFormat(value, pledge(token), rm) : '-');

    return {
        toFixedLtv,
        toFixedToken,
        toFormatToken,
        toFixedLoan,
        toFormatLoan,
        toFixedPledge,
        toFormatPledge,
        toFixedInterest,
        toFormatInterest,
        toFixedLiquidate,
        toFormatLiquidate,
    };
}
